<template>
	<div class="app-list-select-dropdown">
		<app-loader v-if="isLoading" :style="loaderStyle" />
		<div class="app-list-select-dropdown__search">
			<app-input
				ref="$input"
				v-model="filter"
				class="app-list-select-dropdown__search-input"
				:placeholder="$t('search_short')"
				show-add-button
				:add-hint-text="$t('create_and_add_list')"
				@add="onAddElementToNewList"
				@press-enter="enter"
			/>
			<div
				v-if="props.showCloseIcon"
				class="app-list-select-dropdown__close"
			>
				<app-icon
					name="close"
					width="20px"
					color="#D2D2D2"
					hover-color="#499259"
					cursor="pointer"
					:title="$t('close_label')"
					@click="emit('close')"
				/>
			</div>
		</div>
		<div class="app-list-select-dropdown__header">
			<div class="app-list-select-dropdown__header-title">
				{{ $t("available_lists") }}
			</div>
			<div
				class="app-list-select-dropdown__header-drop-btn"
				@click="dropAllLists"
			>
				{{ $t("selector_reset") }}
			</div>
		</div>
		<div
			v-if="filteredLists?.length"
			class="app-list-select-dropdown__body"
			ref="$body"
		>
			<div
				v-for="element in filteredLists"
				class="app-list-select-dropdown__body-item"
				:key="element.Id"
			>
				<app-checkbox
					:style="{ height: '16px', marginRight: '8px' }"
					:value="isListAdded(element.Id)"
					text=""
					@input="onChangeListStage($event, element.Id)"
				/>
				<div
					class="app-list-select-dropdown__body-item-text"
					@click="onToggleByName(element.Id)"
					v-html="highlightSearchText(filter, element.Name)"
				/>
			</div>
		</div>
		<div class="app-list-select-dropdown__body-empty" v-else>
			<div class="app-list-select-dropdown__body-item-text">{{ $t("list_not_found") }}</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import {
	computed,
	nextTick,
	onMounted,
	ref,
	watch
} from "vue";
import AppCheckbox from "@/components/ui-kit/checkbox/AppCheckbox.vue";
import AppInput from "@/components/ui-kit/input/AppInput.vue";
import AppLoader from "@/components/common/AppLoader.vue";
import AppIcon from "@/components/ui-kit/icon/AppIcon.vue";
import { debounce } from "lodash";
import { highlightSearchText } from "@/utils/textHelpers";
import { AnalyticsApi } from "@/ api/AnalyticsApi";

type AnalyticsList = {
	Id: number;
	Name: string;
	Position: number;
};

const props = defineProps<{
	segment: number;
	elementId: number;
	addedLists: { Id: number; Name: string; Position?: number }[];
	showCloseIcon?: boolean;
}>();

const emit = defineEmits(["close", "addList"]);

const $input = ref<typeof AppInput | null>(null);
const $body = ref<HTMLDivElement | null>(null);

const isLoading = ref(true);
const filter = ref("");
const take = 60;
const skip = ref(0);
const canLoadNext = ref(true);
const isInit = ref(false);
const lists = ref<AnalyticsList[] | null>(null);
const existLists = computed(() => props.addedLists ?? []);

const filteredLists = computed(() => {
	if (!lists.value) return [];

	let result: AnalyticsList[] = [];

	if (existLists.value.length) {
		const sorted = [...existLists.value] as AnalyticsList[];
		lists.value.forEach((item) => {
			if (!existLists.value.find((c) => c.Id === item.Id)) {
				sorted.push(item);
			}
		});
		result = sorted;
	} else {
		result = [...lists.value];
	}

	result.sort((a, b) =>
		a.Name.localeCompare(b.Name, "ru", { sensitivity: "base" }),
	);

	if (!filter.value) return result;
	const search = filter.value.toLowerCase();
	return result.filter((item) => item.Name.toLowerCase().includes(search));
});

const loaderStyle = computed(() => ({
	"background-color": "rgba(255, 255, 255, 0.5)",
}));


const enter = () => {
	if (
		filter.value &&
		filteredLists.value.length !== 0 &&
		!existLists.value.find((l) => l.Id === filteredLists.value[0].Id)
	) {
		onChangeListStage(true, filteredLists.value[0].Id);
	}
};

const onFetchLists = async () => {
	try {
		if (canLoadNext.value) {
			isLoading.value = true;
			const listsArray = await AnalyticsApi.getLists(
				filter.value,
				0,
				props.segment,
				props.elementId,
			);
			lists.value =
				skip.value === 0
					? listsArray.Lists
					: lists.value
					? [...lists.value, ...listsArray.Lists]
					: listsArray.Lists;
			skip.value += take;
		}
	} catch (e) {
		console.error(e);
	} finally {
		isLoading.value = false;
	}
};

const onSearch = async () => {
	$body.value?.scrollTo({ top: 0 });
	canLoadNext.value = true;
	skip.value = 0;
	await onFetchLists();
};

const debouncedSearch = debounce(onSearch, 500);

const onAddElementToList = async (listId: number) => {
	try {
		isLoading.value = true;
		await AnalyticsApi.addElementToList(listId, "", props.segment, props.elementId);
		emit("addList");
	} catch (e) {
		console.error(e);
	} finally {
		isLoading.value = false;
	}
};

const onAddElementToNewList = async () => {
	try {
		isLoading.value = true;
		await AnalyticsApi.addElementToList(undefined, filter.value, props.segment, props.elementId);
		emit("addList");
		await onFetchLists();
	} catch (e) {
		console.error(e);
	} finally {
		isLoading.value = false;
	}
};

const onDeleteFromList = async (listId: number) => {
	try {
		isLoading.value = true;
		await AnalyticsApi.removeElementIntoList(listId, props.segment, props.elementId);
		emit("addList");
	} catch (e) {
		console.error(e);
	} finally {
		isLoading.value = false;
	}
};

const dropAllLists = async () => {
	try {
		isLoading.value = true;
		await Promise.allSettled(
			existLists.value.map((l) =>
				AnalyticsApi.removeElementIntoList(l.Id, props.segment, props.elementId),
			),
		);
		emit("addList");
	} catch (e) {
		console.error(e);
	} finally {
		isLoading.value = false;
	}
};

const isListAdded = (listId: number) =>
	!!existLists.value.find((c) => c.Id === listId);

const onChangeListStage = async (stage: boolean, listId: number) => {
	if (!lists.value) return;
	if (stage) {
		await onAddElementToList(listId);
	} else {
		await onDeleteFromList(listId);
	}
};

const onToggleByName = async (listId: number) => {
	await onChangeListStage(!isListAdded(listId), listId);
};

watch(filter, (newVal, oldVal) => {
	if (newVal !== oldVal) debouncedSearch();
});

watch(isInit, (newValue) => {
	if (newValue) {
		nextTick(() => {
			$body.value?.addEventListener("scroll", async () => {
				if (canLoadNext.value && !isLoading.value) {
					if (
						($body.value?.scrollTop ?? 0) + ($body.value?.offsetHeight ?? 0) >
						($body.value?.scrollHeight ?? 0) - 500
					) {
						await onFetchLists();
					}
				}
			});
		});
	}
});

onMounted(async () => {
	await onFetchLists();
	isInit.value = true;
	await nextTick(() => $input.value?.focusInput?.());
});
</script>

<style scoped lang="scss">
@import "@/styles/color.scss";

.app-list-select-dropdown {
	display: flex;
	flex-direction: column;
	position: relative;
	height: 100%;
	padding: 12px 0 0 12px;

	&__search {
		display: flex;
		gap: 8px;
		width: 100%;
		margin-bottom: 8px;
		padding-right: 12px;

		&-input {
			width: 100%;
		}
	}

	&__header {
		display: flex;
		align-items: center;
		justify-content: space-between;
		margin-bottom: 8px;
		padding-right: 12px;
		color: #979797;

		&-title {
			font-size: 12px;
			line-height: 16px;
			font-weight: 600;
		}

		&-drop-btn {
			font-size: 14px;
			line-height: 18px;
			cursor: pointer;

			&:hover {
				color: #396742;
				text-decoration: underline;
			}
		}
	}

	&__body {
		display: flex;
		flex: 1;
		flex-direction: column;
		gap: 8px;
		overflow: auto;

		&-item {
			display: flex;
			font-size: 14px;
			line-height: 18px;
			color: $color-text_main;
			cursor: pointer;

			&-text {
				color: $color-text_main;
				font-size: 14px;
				line-height: 18px;
			}
		}

		&-empty {
			flex-direction: column;
			height: 100%;
			display: flex;
			justify-content: center;
			text-align: center;
		}
	}
}
</style>
